/**
 * Clinical History Tab Component
 * ==============================
 * Displays unified clinical history for a patient:
 * - Events (hospitalizations, ER visits, etc.)
 * - Treatment evolutions
 * - Response evaluations
 * - Clinical updates
 */

import { useState, useEffect } from "react";
import api from "@/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Hospital,
  AlertTriangle,
  TrendingUp,
  Activity,
  Calendar,
  User,
  Clock,
  ChevronRight,
  FileEdit,
  Loader2,
  CheckCircle,
  XCircle,
  Minus,
  ArrowRight,
  CalendarCheck
} from "lucide-react";

const EVENT_ICONS = {
  event: Hospital,
  treatment_evolution: TrendingUp,
  response_evaluation: Activity,
  evaluation_update: FileEdit
};

const EVENT_COLORS = {
  hospitalization: "bg-red-100 text-red-700 border-red-200",
  er_visit: "bg-orange-100 text-orange-700 border-orange-200",
  death: "bg-slate-100 text-slate-700 border-slate-200",
  consultation: "bg-blue-100 text-blue-700 border-blue-200",
  toxicity: "bg-purple-100 text-purple-700 border-purple-200",
  treatment_evolution: "bg-emerald-100 text-emerald-700 border-emerald-200",
  response_evaluation: "bg-cyan-100 text-cyan-700 border-cyan-200",
  evaluation_update: "bg-amber-100 text-amber-700 border-amber-200"
};

const RESPONSE_COLORS = {
  "Réponse complète": "text-green-600",
  "Réponse partielle": "text-emerald-600",
  "Stabilité": "text-blue-600",
  "Progression": "text-red-600"
};

export default function ClinicalHistoryTab({ patientId, onRefresh }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  
  // For editing discharge date
  const [editingDischarge, setEditingDischarge] = useState(null);
  const [dischargeDate, setDischargeDate] = useState("");
  const [savingDischarge, setSavingDischarge] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, [patientId]);

  const fetchHistory = async () => {
    try {
      const res = await api.get(`/clinical/patients/${patientId}/full-history`);
      setHistory(res.data.items || []);
    } catch (err) {
      // error handled by toast
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDischarge = async (eventId) => {
    if (!dischargeDate) {
      toast.error("Veuillez saisir une date de sortie");
      return;
    }
    
    setSavingDischarge(true);
    try {
      await api.put(`/clinical/events/${eventId}/discharge?discharge_date=${dischargeDate}`);
      toast.success("Date de sortie enregistrée");
      setEditingDischarge(null);
      setDischargeDate("");
      fetchHistory();
      if (onRefresh) onRefresh(); // Refresh patient data to update status
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSavingDischarge(false);
    }
  };

  const filteredHistory = filter === "all" 
    ? history 
    : history.filter(item => item.type === filter);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getIcon = (item) => {
    const IconComponent = EVENT_ICONS[item.type] || Activity;
    return <IconComponent className="w-4 h-4" />;
  };

  const getColorClass = (item) => {
    if (item.type === "event") {
      return EVENT_COLORS[item.subType] || EVENT_COLORS.consultation;
    }
    return EVENT_COLORS[item.type] || "bg-slate-100 text-slate-700";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={filter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("all")}
        >
          Tout ({history.length})
        </Button>
        <Button
          variant={filter === "event" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("event")}
          className={filter === "event" ? "bg-red-600 hover:bg-red-700" : ""}
        >
          <Hospital className="w-4 h-4 mr-1" />
          Événements
        </Button>
        <Button
          variant={filter === "treatment_evolution" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("treatment_evolution")}
          className={filter === "treatment_evolution" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
        >
          <TrendingUp className="w-4 h-4 mr-1" />
          Évolutions traitement
        </Button>
        <Button
          variant={filter === "response_evaluation" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("response_evaluation")}
          className={filter === "response_evaluation" ? "bg-cyan-600 hover:bg-cyan-700" : ""}
        >
          <Activity className="w-4 h-4 mr-1" />
          Évaluations réponse
        </Button>
      </div>

      {/* History List */}
      {filteredHistory.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Activity className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Aucun historique clinique</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredHistory.map((item) => (
            <Card key={item.id} className="overflow-hidden">
              <div className={`flex items-start gap-4 p-4 border-l-4 ${
                item.type === "event" && item.subType === "hospitalization" 
                  ? "border-l-red-500" 
                  : item.type === "event" && item.subType === "er_visit"
                  ? "border-l-orange-500"
                  : item.type === "treatment_evolution"
                  ? "border-l-emerald-500"
                  : item.type === "response_evaluation"
                  ? "border-l-cyan-500"
                  : "border-l-slate-300"
              }`}>
                {/* Icon */}
                <div className={`p-2 rounded-lg ${getColorClass(item)}`}>
                  {getIcon(item)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">{item.label}</span>
                    {item.isOngoing && (
                      <Badge variant="destructive" className="text-xs">En cours</Badge>
                    )}
                  </div>
                  
                  {/* Date */}
                  <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(item.date)}
                  </div>

                  {/* Event-specific content */}
                  {item.type === "event" && item.subType === "hospitalization" && (
                    <div className="mt-2 space-y-2">
                      {item.data?.reason && (
                        <p className="text-sm text-slate-600">
                          <span className="font-medium">Motif:</span> {item.data.reason}
                        </p>
                      )}
                      {item.data?.service && (
                        <p className="text-sm text-slate-600">
                          <span className="font-medium">Service:</span> {item.data.service}
                        </p>
                      )}
                      
                      {/* Discharge date */}
                      {item.dischargeDate ? (
                        <div className="flex items-center gap-2 text-sm text-green-600">
                          <CalendarCheck className="w-4 h-4" />
                          Sortie le {new Date(item.dischargeDate).toLocaleDateString("fr-FR")}
                        </div>
                      ) : item.isOngoing ? (
                        <div className="mt-2">
                          {editingDischarge === item.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                type="date"
                                value={dischargeDate}
                                onChange={(e) => setDischargeDate(e.target.value)}
                                className="w-40"
                              />
                              <Button
                                size="sm"
                                onClick={() => handleSaveDischarge(item.id)}
                                disabled={savingDischarge}
                              >
                                {savingDischarge ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingDischarge(null)}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingDischarge(item.id);
                                setDischargeDate("");
                              }}
                            >
                              <CalendarCheck className="w-4 h-4 mr-1" />
                              Ajouter date de sortie
                            </Button>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}

                  {item.type === "event" && item.subType === "er_visit" && item.data?.reason && (
                    <p className="text-sm text-slate-600 mt-2">
                      <span className="font-medium">Motif:</span> {item.data.reason}
                    </p>
                  )}
                  {item.type === "event" && item.data?.treatmentRelated && (
                    <p className="text-sm text-slate-600 mt-1" data-testid={`clinical-event-treatment-related-${item.id}`}>
                      <span className="font-medium">Lien traitement:</span> {item.data.treatmentRelated}
                      {item.data.suspectedMolecule ? ` · ${item.data.suspectedMolecule}` : ""}
                    </p>
                  )}

                  {/* Treatment evolution content */}
                  {item.type === "treatment_evolution" && (
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span className="text-slate-500">{item.data.oldValue || "—"}</span>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <span className="font-medium text-emerald-600">{item.data.newValue}</span>
                      {item.data.reason && (
                        <span className="text-slate-500 ml-2">({item.data.reason})</span>
                      )}
                      {item.data.doseModificationType && (
                        <span className="text-slate-500 ml-2" data-testid={`dose-modification-summary-${item.id}`}>
                          {item.data.doseModificationType} · {item.data.doseModificationMolecule || "molécule non précisée"}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Response evaluation content */}
                  {item.type === "response_evaluation" && (
                    <div className="mt-2 space-y-1">
                      <div className={`font-medium ${RESPONSE_COLORS[item.data.status] || ""}`}>
                        {item.data.status}
                      </div>
                      {item.data.source && (
                        <p className="text-sm text-slate-500">
                          Source: {item.data.source}
                        </p>
                      )}
                      {(item.data.criteria || item.data.validationLevel) && (
                        <p className="text-sm text-slate-500" data-testid={`response-evaluation-quality-${item.id}`}>
                          {[item.data.criteria, item.data.validationLevel].filter(Boolean).join(" · ")}
                        </p>
                      )}
                      {item.data.notes && (
                        <p className="text-sm text-slate-600 mt-1">{item.data.notes}</p>
                      )}
                    </div>
                  )}

                  {/* Evaluation update content */}
                  {item.type === "evaluation_update" && (
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span className="text-slate-500">
                        {Array.isArray(item.data.oldValue) 
                          ? item.data.oldValue.join(", ") || "—"
                          : item.data.oldValue || "—"}
                      </span>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <span className="font-medium text-amber-600">
                        {Array.isArray(item.data.newValue) 
                          ? item.data.newValue.join(", ")
                          : item.data.newValue}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
